import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, Save } from 'lucide-react';
import { useAuth } from '@/core/auth/AuthContext';
import { useLaborRates } from '../hooks/useLaborRates';
import { Input, Select } from '@/components/ui';
import type { UserLaborRate, LaborRateCreatePayload } from '../types/laborRates.types';
import { supabase } from '@/core/database/supabase';

interface LaborRateModalProps {
  isOpen: boolean;
  onClose: () => void;
  rate?: UserLaborRate;
}

export function LaborRateModal({ isOpen, onClose, rate }: LaborRateModalProps) {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const { createRate, updateRate, isCreating, isUpdating } = useLaborRates();
  const isSaving = isCreating || isUpdating;

  const [users, setUsers] = useState<{id: string, full_name: string}[]>([]);

  useEffect(() => {
    if (isOpen) {
      // Fetch users for dropdown
      supabase.from('users').select('id, full_name').eq('company_id', companyId)
        .then(({ data }) => setUsers(data || []));
    }
  }, [isOpen, companyId]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<LaborRateCreatePayload>({
    defaultValues: {
      company_id: companyId,
      user_id: '',
      rate_type: 'daily',
      amount: 0,
      effective_from: '',
      effective_to: '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (rate) {
        reset({
          company_id: rate.company_id,
          user_id: rate.user_id,
          rate_type: rate.rate_type,
          amount: rate.amount,
          effective_from: rate.effective_from || '',
          effective_to: rate.effective_to || '',
        });
      } else {
        reset({
          company_id: companyId,
          user_id: '',
          rate_type: 'daily',
          amount: 0,
          effective_from: new Date().toISOString().split('T')[0],
          effective_to: '',
        });
      }
    }
  }, [isOpen, rate, reset, companyId]);

  if (!isOpen) return null;

  const onSubmit = async (data: LaborRateCreatePayload) => {
    const payload = {
      ...data,
      effective_from: data.effective_from || null,
      effective_to: data.effective_to || null,
    };

    if (rate) {
      await updateRate({ id: rate.id, payload });
    } else {
      await createRate(payload);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">
            {rate ? 'Cập nhật đơn giá' : 'Thiết lập đơn giá nhân công'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <form id="labor-rate-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                Nhân sự <span className="text-rose-500">*</span>
              </label>
              <Select 
                {...register('user_id', { required: 'Vui lòng chọn nhân sự' })} 
                className="w-full sm:text-sm text-xs"
                disabled={!!rate} // Cannot change user once created
              >
                <option value="">-- Chọn nhân sự --</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.full_name}</option>
                ))}
              </Select>
              {errors.user_id && <p className="mt-1 text-xs text-rose-500">{errors.user_id.message}</p>}
            </div>

            <div>
              <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                Hình thức tính <span className="text-rose-500">*</span>
              </label>
              <Select {...register('rate_type')} className="w-full sm:text-sm text-xs">
                <option value="hourly">Theo giờ</option>
                <option value="daily">Theo ngày (Công)</option>
                <option value="monthly">Khóan tháng</option>
                <option value="custom">Tùy chỉnh</option>
              </Select>
            </div>

            <div>
              <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                Đơn giá (VNĐ) <span className="text-rose-500">*</span>
              </label>
              <Input
                type="number"
                min={0}
                {...register('amount', { required: 'Vui lòng nhập đơn giá', valueAsNumber: true })}
                placeholder="Ví dụ: 300000"
                className="w-full sm:text-sm text-xs font-mono"
              />
              {errors.amount && <p className="mt-1 text-xs text-rose-500">{errors.amount.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                  Hiệu lực từ
                </label>
                <Input
                  type="date"
                  {...register('effective_from')}
                  className="w-full sm:text-sm text-xs"
                />
              </div>
              <div>
                <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                  Đến ngày
                </label>
                <Input
                  type="date"
                  {...register('effective_to')}
                  className="w-full sm:text-sm text-xs"
                />
              </div>
            </div>
          </form>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-200 bg-slate-50 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 sm:text-sm text-xs font-medium text-slate-700 hover:bg-slate-200 rounded-md transition-colors"
          >
            Hủy bỏ
          </button>
          <button
            type="submit"
            form="labor-rate-form"
            disabled={isSaving}
            className="inline-flex items-center justify-center gap-2 px-6 py-2 sm:text-sm text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Đang lưu...' : 'Lưu lại'}
          </button>
        </div>
      </div>
    </div>
  );
}
